import React, { useCallback, useEffect, useMemo, useState } from 'react';
import 'leaflet/dist/leaflet.css';
import { jwtDecode } from "jwt-decode";
import Cookies from 'js-cookie';
import { useTaskTypes } from '../../../hooks/useTaskTypes.js';
import { useTaskClientCriteriaPreferences } from '../../../hooks/useTaskClientCriteriaPreferences.js';
import GenericTables from '../../../shared/components/ui/GenericTables.jsx';
import PageHeader from '../../../shared/components/ui/PageHeader.jsx';
import TintedButton from '../../../shared/components/ui/TintedButton.jsx';
import GenericFilters from '../../../shared/components/ui/GenericFilters.jsx';
import ActionMenu from '../../../shared/components/ui/ActionMenu.jsx';
import ModalCreationEditing from '../../../shared/components/ui/ModalCreationEditing.jsx';
import Tooltip from '../../../shared/components/ui/Tooltip.jsx';
import TasksCalendarView, { ensureDate, formatYmd } from '../components/TasksCalendarView.jsx';
import {
  buildDefaultTaskFilters,
  useTaskManagementFiltersStore
} from '../../../stores/taskManagementFiltersStore.js';
import { userService } from '../../../services/users/userService.js';
import { taskService } from '../../../services/tasks/taskService.js';
import { teamService } from '../../../services/teams/teamService.js';
import {
  PencilSimple,
  FileText,
  Tag,
  Lightning,
  Calendar,
  CalendarBlank,
  CalendarCheck,
  CalendarDots,
  ArrowsClockwise,
  User,
  Envelope,
  Phone,
  IdentificationCard,
  House,
  Car,
  FloppyDisk,
  Info,
  Plus,
  ChartBar,
  ArrowsOutCardinal,
  Trash,
  Eye,
  Pencil,
  Check,
  Checks,
  Clock,
  Funnel,
  CaretDown,
  ListBullets,
  Gear,
  ArrowUp,
  ArrowDown,
  PushPin,
  PushPinSlash

} from '@phosphor-icons/react';
import { buildFixedTeamPayload, taskHasTeam } from '../utils/taskFixedTeam.js';
import Alerts from '../../../shared/components/ui/Alerts.jsx';
import { getTaskStatusBadgeStyles, getTaskStatusLabel } from '../../../constants/taskColors.js';
import { CLIENT_SEARCH_CRITERIA } from '../../../constants/clientSearchCriteria.js';
import { useGuidedTourStep } from '../../../shared/hooks/useGuidedTour.js';
import { buildTaskModalSections } from '../taskModalSections.js';

// Función para normalizar coordenadas (fuera del componente para reutilización)
const normalizeCoordinates = (lat, lng) => {
  // Normalizar latitud: debe estar entre -90 y 90
  let normalizedLat = parseFloat(lat);
  if (isNaN(normalizedLat)) return null;
  normalizedLat = Math.max(-90, Math.min(90, normalizedLat));

  // Normalizar longitud: debe estar entre -180 y 180
  let normalizedLng = parseFloat(lng);
  if (isNaN(normalizedLng)) return null;
  // Si la longitud está fuera del rango, ajustarla al rango válido
  while (normalizedLng > 180) normalizedLng -= 360;
  while (normalizedLng < -180) normalizedLng += 360;

  // Redondear a 6 decimales (precisión de ~0.1 metros)
  return {
    lat: Math.round(normalizedLat * 1000000) / 1000000,
    lng: Math.round(normalizedLng * 1000000) / 1000000
  };
};

const hasCoordinates = (lat, lng) => (
  lat !== null && lat !== undefined && lat !== ''
  && lng !== null && lng !== undefined && lng !== ''
);

const toTaskModalValues = (task = {}) => ({
  ...task,
  taskType: task.taskType || task.taskTypeId || '',
  client: {
    ...(task.client || {}),
    coordinates: {
      lat: task.client?.lat ?? null,
      lng: task.client?.lng ?? null
    }
  }
});

const fromTaskModalValues = (values = {}) => {
  const { coordinates: clientCoordinates, ...client } = values.client || {};

  return {
    ...values,
    client: {
      ...client,
      lat: clientCoordinates?.lat ?? null,
      lng: clientCoordinates?.lng ?? null
    }
  };
};

const CALENDAR_FETCH_PAGE_SIZE = 5000;

const formatFilterDate = (date) => {
  const safeDate = date instanceof Date && !Number.isNaN(date.getTime()) ? date : new Date(date);
  const year = safeDate.getFullYear();
  const month = String(safeDate.getMonth() + 1).padStart(2, '0');
  const day = String(safeDate.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const parseFilterDate = (value) => {
  if (!value || typeof value !== 'string') return null;
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return null;
  const parsed = new Date(year, month - 1, day);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const isFullMonthCustomRange = (filters) => {
  if (filters?.dateType !== 'custom' || !filters?.customDateFrom || !filters?.customDateTo) {
    return false;
  }

  const fromDate = parseFilterDate(filters.customDateFrom);
  const toDate = parseFilterDate(filters.customDateTo);

  if (!fromDate || !toDate) {
    return false;
  }

  if (
    fromDate.getFullYear() !== toDate.getFullYear()
    || fromDate.getMonth() !== toDate.getMonth()
  ) {
    return false;
  }

  const firstDayOfMonth = new Date(fromDate.getFullYear(), fromDate.getMonth(), 1);
  const lastDayOfMonth = new Date(fromDate.getFullYear(), fromDate.getMonth() + 1, 0);

  return (
    formatFilterDate(fromDate) === formatFilterDate(firstDayOfMonth)
    && formatFilterDate(toDate) === formatFilterDate(lastDayOfMonth)
  );
};

const resolveInitialCalendarMode = (storedFilters) => {
  return storedFilters?.dateType === 'month' || isFullMonthCustomRange(storedFilters)
    ? 'month'
    : 'day';
};

const resolveInitialCalendarDate = (storedFilters) => {
  if (storedFilters?.dateType === 'month' || isFullMonthCustomRange(storedFilters)) {
    return parseFilterDate(storedFilters.date)
      || parseFilterDate(storedFilters.customDateFrom)
      || new Date();
  }

  if (storedFilters?.dateType === 'custom' && storedFilters.customDateFrom === storedFilters.customDateTo) {
    return parseFilterDate(storedFilters.customDateFrom) || new Date();
  }

  if (storedFilters?.dateType === 'today') {
    return new Date();
  }

  return parseFilterDate(storedFilters?.date) || new Date();
};

const getMonthFilterRange = (date) => {
  const safeDate = date instanceof Date && !Number.isNaN(date.getTime()) ? date : new Date(date);
  const firstDay = new Date(safeDate.getFullYear(), safeDate.getMonth(), 1);
  const lastDay = new Date(safeDate.getFullYear(), safeDate.getMonth() + 1, 0);

  return {
    from: formatFilterDate(firstDay),
    to: formatFilterDate(lastDay)
  };
};

const TaskManagement = ({ onViewTask = null }) => {
  const storedFilters = useTaskManagementFiltersStore((state) => state.filters);
  const storedCurrentPage = useTaskManagementFiltersStore((state) => state.currentPage);
  const storedPageSize = useTaskManagementFiltersStore((state) => state.pageSize);
  const storedViewMode = useTaskManagementFiltersStore((state) => state.viewMode);
  const setStoredFilters = useTaskManagementFiltersStore((state) => state.setFilters);
  const setStoredCurrentPage = useTaskManagementFiltersStore((state) => state.setCurrentPage);
  const setStoredPageSize = useTaskManagementFiltersStore((state) => state.setPageSize);
  const setStoredViewMode = useTaskManagementFiltersStore((state) => state.setViewMode);

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success', origin: '' });
  const { taskTypes, loading: loadingTaskTypes } = useTaskTypes();
  const {
    orderedVisibleCriteria,
    visibleCriteria: clientCriteriaVisible,
    order: clientCriteriaOrder,
    setVisibleCriteria: setClientCriteriaVisible,
    setOrder: setClientCriteriaOrder,
    resetToDefaults: resetClientCriteriaPreferences
  } = useTaskClientCriteriaPreferences();
  const [showClientCriteriaSettings, setShowClientCriteriaSettings] = useState(false);
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    scheduledDate: '',
    client: {
      name: '',
      email: '',
      phone: '',
      secondaryPhone: '',
      cuil: '',
      address: '',
      lat: null,
      lng: null
    },
    teamId: '',
    taskType: '',
    ticketNumber: '',
    estimatedDuration: '',
    isCorporate: false,
    fixedTeam: false,
    isTimeInHours: true
  });
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [editTask, setEditTask] = useState(null);
  const [originalEditTask, setOriginalEditTask] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [deleteTaskId, setDeleteTaskId] = useState(null);
  const [filters, setFilters] = useState(() => storedFilters || buildDefaultTaskFilters());
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isTaskModalSubmitting, setIsTaskModalSubmitting] = useState(false);
  const [taskModalResetKey, setTaskModalResetKey] = useState(0);
  const [showAlerts, setshowAlerts] = useState(false);
  const [showAlertsContenido, setshowAlertsContenido] = useState('');

  const isRepairTaskType = (taskTypeName) => {
    if (!taskTypeName) return false;
    return taskTypes.find((type) => type.name === taskTypeName)?.categoryName !== 'instalacion';
  };

  const [showAlertsDeleteTitle, setShowAlertsDeleteTitle] = useState('');
  const [showAlertsDelete, setShowAlertsDelete] = useState(false);
  const [showAlertsDeleteContenido, setShowAlertsDeleteContenido] = useState('');

  const [showAlertsForceCreate, setShowAlertsForceCreate] = useState(false);
  const [showAlertsForceCreateDate, setShowAlertsForceCreateDate] = useState('');
  const [showFixedTeamChangeAlert, setShowFixedTeamChangeAlert] = useState(false);
  const [forceCreateOrigin, setForceCreateOrigin] = useState(null);
  const [pendingForceCreateTask, setPendingForceCreateTask] = useState(null);
  const [pendingReassignTaskId, setPendingReassignTaskId] = useState(null);
  const [pendingReassignTeamId, setPendingReassignTeamId] = useState('');

  // Reasignación masiva por equipo - 3 pasos
  const [bulkSourceTeamId, setBulkSourceTeamId] = useState('');
  const [bulkSourceTeamName, setBulkSourceTeamName] = useState('');
  const [bulkDestTeamId, setBulkDestTeamId] = useState('');
  const [bulkDestTeamName, setBulkDestTeamName] = useState('');
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkStep, setBulkStep] = useState(1); // 1: origen, 2: tareas, 3: destino

  // Exportación CSV
  const [exporting, setExporting] = useState(false);
  const [csvDateFrom, setCsvDateFrom] = useState('');
  const [csvDateTo, setCsvDateTo] = useState('');
  const [csvFilterStatus, setCsvFilterStatus] = useState('');
  const [csvFilterTeam, setCsvFilterTeam] = useState('');
  const [showCsvModal, setShowCsvModal] = useState(false);

  const [showFilters, setShowFilters] = useState(false);

  // Estados de paginación
  const [pageSize, setPageSize] = useState(() => storedPageSize || 10);
  const [currentPage, setCurrentPage] = useState(() => storedCurrentPage || 1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResponses, setTotalResponses] = useState(0);
  const [viewMode, setViewMode] = useState(() => storedViewMode || 'list');
  // Recorrido guiado: en el paso de filtros (openFilters) el panel se monta desplegado, y en los
  // pasos del alta (modal: 'task-create') se abre el formulario, que se cierra al salir de ellos.
  const [tourStep, setTourStep] = useState(null);
  useGuidedTourStep((step) => {
    if (step?.modal === 'task-create' && tourStep?.modal !== 'task-create') handleCreateForm();
    if (step?.modal !== 'task-create' && tourStep?.modal === 'task-create') setShowCreateModal(false);
    setTourStep(step);
  });
  const [calendarMode, setCalendarMode] = useState(() => resolveInitialCalendarMode(storedFilters));
  const [calendarDate, setCalendarDate] = useState(() => resolveInitialCalendarDate(storedFilters));
  const [teamLoadsRefreshToken, setTeamLoadsRefreshToken] = useState(0);

  // Recalcula el resumen por equipo del calendario. Se le pasan todas las fechas que
  // toca la operacion: una edicion que mueve la tarea de dia afecta al origen y al destino.
  const refreshTeamLoads = useCallback((...affectedDates) => {
    if (viewMode !== 'calendar' || calendarMode !== 'day') {
      return;
    }

    const visibleDayKey = formatYmd(ensureDate(calendarDate));
    const touchesVisibleDay = affectedDates.some(
      (date) => date && formatYmd(ensureDate(date)) === visibleDayKey
    );

    if (!touchesVisibleDay) {
      return;
    }

    setTeamLoadsRefreshToken((currentToken) => currentToken + 1);
  }, [calendarDate, calendarMode, viewMode]);

  const [activeTeams, setActiveTeams] = useState([])
  const [pasiveTeams, setPasiveTeams] = useState('')

  useEffect(() => {
    setStoredFilters(filters);
  }, [filters, setStoredFilters]);

  useEffect(() => {
    setStoredCurrentPage(currentPage);
  }, [currentPage, setStoredCurrentPage]);

  useEffect(() => {
    setStoredPageSize(pageSize);
  }, [pageSize, setStoredPageSize]);

  const fetchTeams = async () => {
    try {
      const result = await teamService.getTeams({ hasGeoData: false });
      setTeams(result);

    } catch (err) {
      console.error("Error al cargar equipos:", err);
    }
  };

  // Función para detectar el rol del usuario
  const detectUserRole = () => {
    try {
      const token = Cookies.get('sessionToken');
      if (token) {
        const decoded = jwtDecode(token);
        return decoded.role;
      }
    } catch (error) {
      console.error('Error detecting user role:', error);
    }
    return null;
  };

  const currentUserRole = userRole || detectUserRole();
  const canEditTask = (task) => currentUserRole !== 'operator' || task.status === 'pendiente';
  const canDeleteTask = (task) => (
    currentUserRole !== 'operator' ||
    task?.status === 'pendiente'
  );

  // Función para mostrar notificaciones
  const showNotification = (message, type = 'success', origin = '') => {
    setNotification({ show: true, message, type, origin });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: 'success', origin: '' });
    }, 3000);
  };

  const syncCalendarFilters = (nextMode, nextDate) => {
    const safeDate = nextDate instanceof Date && !Number.isNaN(nextDate.getTime()) ? nextDate : new Date(nextDate);
    const nextDateValue = formatFilterDate(safeDate);

    setCalendarMode(nextMode);
    setCalendarDate(safeDate);
    setFilters((previousFilters) => {
      if (nextMode === 'month') {
        const { from, to } = getMonthFilterRange(safeDate);
        return {
          ...previousFilters,
          dateType: 'custom',
          customDateFrom: from,
          customDateTo: to,
          date: nextDateValue
        };
      }

      return {
        ...previousFilters,
        dateType: 'custom',
        customDateFrom: nextDateValue,
        customDateTo: nextDateValue,
        date: nextDateValue
      };
    });
    setCurrentPage(1);
  };

  const handleTaskViewModeChange = (nextViewMode) => {
    if (nextViewMode === 'calendar' && viewMode !== 'calendar') {
      setCalendarMode(resolveInitialCalendarMode(filters));
      setCalendarDate(resolveInitialCalendarDate(filters));
    }

    setViewMode(nextViewMode);
    setStoredViewMode(nextViewMode);
  };

  const fetchTasks = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      // Usar parseInt para asegurar que son números
      const sizeValue = parseInt(pageSize) || 10;
      const pageValue = parseInt(currentPage) || 1;
      const effectivePageSize = viewMode === 'calendar' ? CALENDAR_FETCH_PAGE_SIZE : sizeValue;
      const effectivePage = viewMode === 'calendar' ? 1 : pageValue;
      const isMonthlyRange = filters.dateType === 'month' || isFullMonthCustomRange(filters);

      // Backend espera page 0-indexed: page 1 → 0, page 2 → 1, etc
      params.append('pageSize', effectivePageSize);
      params.append('pageCurrent', effectivePage - 1);

      // Enviar todos los filtros al backend
      if (filters.clientSearchCriteria && filters.clientSearchValue) {
        params.append(filters.clientSearchCriteria, filters.clientSearchValue);
      }
      if (filters.type) params.append('type', filters.type);
      if (filters.team) params.append('team', filters.team);
      if (filters.status) params.append('status', filters.status);
      if (currentUserRole === 'admin' && filters.validation) params.append('isValidated', filters.validation);
      if (filters.dateType && filters.dateType !== 'all') params.append('dateType', filters.dateType);
      if (
        filters.date
        && !isMonthlyRange
        && (viewMode === 'calendar' || filters.dateType === 'today')
      ) {
        params.append('date', filters.date);
      }
      if (filters.dateType === 'custom') {
        if (filters.customDateFrom) params.append('dateFrom', filters.customDateFrom);
        if (filters.customDateTo) params.append('dateTo', filters.customDateTo);
      }
      if (filters.dateType === '') { params.append('dateType', 'today'); }
      if (filters.requiresReschedule !== undefined && filters.requiresReschedule !== 'all') params.append('requiresReschedule', filters.requiresReschedule);
      if (filters.hasVisit !== undefined && filters.hasVisit !== 'all') params.append('hasVisit', filters.hasVisit);
      params.append('accurateTotal', 'true');

      const response = await taskService.getTasks(
        Object.fromEntries(params.entries())
      );
      if (response) {
        const responses = response.tasks || [];
        setTasks(responses);

        // Actualizar paginación desde el backend
        if (response.pagination) {
          const totalItems = Number(response.pagination.totalItems) || 0;
          const pageSize = Number(response.pagination.pageSize) || effectivePageSize;

          setTotalResponses(totalItems);

          // Calcular totalPages correctamente: Math.ceil(total / pageSize)
          const calculatedPages = totalItems > 0 ? Math.ceil(totalItems / pageSize) : 1;
          setTotalPages(calculatedPages);
        } else {
          setTotalResponses(responses.length);
          setTotalPages(1);
        }
      } else {
        setError('Error al cargar tareas');
      }
    } catch (err) {
      console.error("Error al cargar tareas:", err);
      setError('Error al cargar tareas');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const usersResult = await userService.getUsers();
      setUsers(usersResult);
      setTechnicians(usersResult.filter(u => u.role === 'technician'));
    } catch (err) {
      console.error("Error al cargar usuarios:", err);
      setUsers([]);
    }
  };

  useEffect(() => {
    // Detectar rol del usuario
    setUserRole(detectUserRole());
  }, []);

  useEffect(() => {
    if (filters.dateType === 'month' || isFullMonthCustomRange(filters)) {
      setCalendarMode('month');
      setCalendarDate(
        parseFilterDate(filters.date)
        || parseFilterDate(filters.customDateFrom)
        || new Date()
      );
      return;
    }

    if (filters.dateType === 'today') {
      setCalendarMode('day');
      setCalendarDate(new Date());
      return;
    }

    if (
      filters.dateType === 'custom'
      && filters.customDateFrom
      && filters.customDateFrom === filters.customDateTo
    ) {
      setCalendarMode('day');
      setCalendarDate(
        parseFilterDate(filters.customDateFrom)
        || parseFilterDate(filters.date)
        || new Date()
      );
    }
  }, [filters.dateType, filters.customDateFrom, filters.customDateTo, filters.date]);

  // Actualizar taskType por defecto cuando se carguen los tipos de tareas
  useEffect(() => {
    if (taskTypes.length > 0 && !newTask.taskType) {
      setNewTask(prev => ({
        ...prev,
        taskType: taskTypes[0].name
      }));
    }
  }, [taskTypes]);

  const handleCreateForm = () => {
    setNewTask({
      title: '',
      description: '',
      priority: '',
      taskType: '',
      client: {
        name: '',
        email: '',
        phone: '',
        secondaryPhone: '',
        cuil: '',
        address: '',
        lat: null,
        lng: null
      },
      teamId: '',
      scheduledDate: '',
      estimatedDuration: '',
      isCorporate: false,
      fixedTeam: false,
      isTimeInHours: true,
      ticketNumber: '',
    });
    setTaskModalResetKey((currentKey) => currentKey + 1);
    setShowCreateModal(true);
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setNewTask({
      title: '',
      description: '',
      priority: 'media',
      taskType: taskTypes.length > 0 ? taskTypes[0].name : 'configuracion_equipos',
      client: {
        name: '',
        email: '',
        phone: '',
        secondaryPhone: '',
        cuil: '',
        address: '',
        lat: null,
        lng: null
      },
      teamId: '',
      scheduledDate: '',
      estimatedDuration: '',
      isCorporate: false,
      fixedTeam: false,
      isTimeInHours: true,
      ticketNumber: ''
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      try {
        // Cargar todas las datos en paralelo
        await Promise.all([
          fetchTasks(),
          fetchTeams(),
          fetchUsers(),
        ]);
      } catch (err) {
        console.error("Error al cargar datos:", err);
      } finally {
        setLoading(false);
      }
    };

    // Inicializar fechas de CSV con el mes actual
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    setCsvDateFrom(firstDayOfMonth.toISOString().split('T')[0]);
    setCsvDateTo(lastDayOfMonth.toISOString().split('T')[0]);

    fetchData();
  }, []); // Solo ejecutar una vez al montar el componente

  // Validación reutilizable para create y edit
  const validateTask = (task, origin = 'create') => {
    if (!task.title || !task.title.toString().trim()) {
      return { ok: false, message: 'El título es requerido' };
    }
    if (!task.scheduledDate) {
      return { ok: false, message: 'La fecha programada es requerida' };
    }
    if (!task.client || !task.client.name || !task.client.name.toString().trim()) {
      return { ok: false, message: 'El nombre del cliente es requerido' };
    }
    // teléfono: solo dígitos y longitud mínima
    const phone = (task.client.phone || '').toString();
    if (!phone.match(/^\d+$/)) {
      return { ok: false, message: 'El teléfono del cliente debe ser un número' };
    }
    if (phone.length < 10) {
      return { ok: false, message: 'El teléfono del cliente debe tener al menos 10 dígitos' };
    }
    // CUIL: requerido, solo dígitos y longitud mínima
    const cuil = (task.client.cuil || '').toString();
    if (!cuil || !cuil.trim()) {
      return { ok: false, message: 'El DNI/CUIL del cliente es requerido' };
    }
    if (!cuil.match(/^\d+$/)) {
      return { ok: false, message: 'El DNI/CUIL del cliente debe ser un número' };
    }
    if (cuil.length < 7) {
      return { ok: false, message: 'El DNI/CUIL del cliente debe tener mínimo 7 dígitos' };
    }
    if (!task.client.address || !task.client.address.toString().trim()) {
      return { ok: false, message: 'La dirección del cliente es requerida' };
    }
    if (!hasCoordinates(task.client.lat, task.client.lng)) {
      return { ok: false, message: 'La ubicación del cliente es requerida' };
    }
    const taskTypeForValidation = task?.taskTypeId || task?.taskType;
    const requiresTicketNumber = isRepairTaskType(taskTypeForValidation);
    const ticketNumber = (task?.ticketNumber || '').toString().trim();

    if (requiresTicketNumber && !ticketNumber) {
      return { ok: false, message: 'El número de ticket es requerido para tareas de reparación' };
    }

    if (ticketNumber && !ticketNumber.match(/^\d+$/)) {
      return { ok: false, message: 'El número de ticket debe ser un número' };
    }
    if (!task.priority){
      return { ok: false, message: 'La prioridad es requerida' };
    }
    const taskTypeValue = task?.taskTypeId || task?.taskType;
    if (!taskTypeValue) {
      return { ok: false, message: 'El tipo de tarea es requerido' };
    }
    if (!task.teamId){
      return { ok: false, message: 'El equipo es requerido' };
    }

    return { ok: true };
  };

  const handleAddTask = async (forceCreate = false, payloadOverride = null) => {
    try {
      // Validar newTask de forma centralizada
      const taskToValidate = payloadOverride || newTask;
      const validation = validateTask(taskToValidate, 'create');
      if (!validation.ok) {
        showNotification(validation.message, 'error', 'tasks');
        return;
      }

      setIsTaskModalSubmitting(true);

      const payload = payloadOverride ? { ...payloadOverride } : { ...newTask };
      // Si no es de alta complejidad, limpiar los campos relacionados
      if (!payload.isCorporate) {
        payload.estimatedDuration = null;
        payload.isTimeInHours = null;
      } else {
        // Convertir estimatedDuration a número si existe
        payload.estimatedDuration = payload.estimatedDuration ? Number(payload.estimatedDuration) : null;
      }
      // El toggle solo se muestra con equipo; sin equipo no puede quedar fija
      payload.fixedTeam = Boolean(payload.teamId) && payload.fixedTeam === true;
      payload.forceCreate = forceCreate || payload.forceCreate === true;
      // Los nombres de equipo se envían directamente como teamId
      // El backend se encargará de resolverlos
      const result = await taskService.createTask(payload);


      if (result) {
        fetchTasks();
        refreshTeamLoads(payload.scheduledDate);
        setNewTask({
          title: '',
          description: '',
          priority: 'media',
          taskType: taskTypes.length > 0 ? taskTypes[0].name : 'configuracion_equipos',
          client: {
            name: '',
            email: '',
            phone: '',
            secondaryPhone: '',
            cuil: '',
            address: '',
            lat: null,
            lng: null
          },
          teamId: '',
          scheduledDate: '',
          estimatedDuration: '',
          isCorporate: false,
          fixedTeam: false,
          isTimeInHours: true,
          ticketNumber: '',
        });
        setShowCreateModal(false);
        setPendingForceCreateTask(null);
        setForceCreateOrigin(null);
        if(result.teamId !== null)
          showNotification('Tarea creada exitosamente', 'success');
        else showNotification('Tarea creada sin equipo', 'success');
      }
    } catch (error) {
      if (error?.message?.includes('inhabilitado') && userRole === 'admin') {
        setPendingForceCreateTask({ ...newTask });
        setForceCreateOrigin('create');
        setShowAlertsForceCreateDate(newTask.scheduledDate);
        setShowAlertsForceCreate(true);
        return;
      }
      console.error('Error creando tarea:', error);
      showNotification(error.message, 'error', 'tasks');
    } finally {
      setIsTaskModalSubmitting(false);
    }
  };

  const handleBulkAssign = async (forceCreate = false) => {
    try {
      if (!bulkDestTeamId) {
        showNotification('Debe seleccionar un equipo destino', 'error', 'reassignment');
        return;
      }

      if (selectedTasks.length === 0) {
        showNotification('Debe seleccionar al menos una tarea', 'error', 'reassignment');
        return;
      }

      if (bulkSourceTeamId === bulkDestTeamId) {
        showNotification('El equipo destino no puede ser el mismo que el origen', 'error', 'reassignment');
        return;
      }

      setBulkLoading(true);
      // Se capturan antes de operar: setSelectedTasks([]) corre antes del refresco
      const affectedDates = selectedTasks
        .map((taskId) => tasks.find((task) => task.id === taskId)?.scheduledDate)
        .filter(Boolean);

      const res = await taskService.bulkAssignTeam({
        teamId: bulkDestTeamId,
        taskIds: selectedTasks,
        forceCreate,
      });

      showNotification(res.message || `Tareas reasignadas: ${res.updatedCount || 0}`);
      setBulkLoading(false);
      setSelectedTasks([]);
      setBulkSourceTeamId('');
      setBulkSourceTeamName('');
      setBulkDestTeamId('');
      setBulkDestTeamName('');
      setBulkStep(1);
      setShowBulkModal(false);
      fetchTasks();
      fetchTeams();
      // Una sola llamada con todas las fechas: un unico refresco por operacion
      refreshTeamLoads(...affectedDates);
    } catch (err) {
      console.error('Error en reasignación masiva:', err);
      setBulkLoading(false);
      if (err?.message?.includes('inhabilitado') && userRole === 'admin') {
        setForceCreateOrigin('bulk');
        setShowAlertsForceCreateDate(new Date());
        setShowAlertsForceCreate(true);
        return;
      }

      showNotification(err.message, 'error', 'reassignment');
    }
  };

  const handleIndividualReassign = async (taskId, newTeamId, forceCreate = false) => {
    try {
      // Se busca antes del await: despues fetchTasks() ya reemplazo el array
      const reassignedTask = tasks.find((task) => task.id === taskId);

      // Si newTeamId está vacío, enviar null para quitar el equipo
      await taskService.reassignTaskTeam(
        taskId,
        newTeamId === '' ? null : newTeamId,
        forceCreate
      );

      showNotification('Equipo reasignado exitosamente', 'success');
      fetchTasks();
      refreshTeamLoads(reassignedTask?.scheduledDate);
    } catch (err) {
      console.error('Error reasignando equipo:', err);
      if (err?.message?.includes('inhabilitado') && userRole === 'admin') {
        const pendingTask = tasks.find((task) => task.id === taskId);
        setPendingReassignTaskId(taskId);
        setPendingReassignTeamId(newTeamId);
        setForceCreateOrigin('reassign');
        setShowAlertsForceCreateDate(pendingTask?.scheduledDate || '');
        setShowAlertsForceCreate(true);
        return;
      }

      const notify = err.message.includes('fija') ? err.message : err.message.split("tarea")[0] + "tarea";
      showNotification(notify, 'error');
    }
  };

  const toggleTaskSelection = (taskId) => {
    setSelectedTasks(prev =>
      prev.includes(taskId)
        ? prev.filter(id => id !== taskId)
        : [...prev, taskId]
    );
  };

  const selectAllTasks = () => {
    if (bulkStep === 2 && bulkSourceTeamId) {
      // En el paso 2, solo seleccionar tareas pendientes del equipo origen
      const pendingTasks = tasks.filter(task => {
        // Verificar si la tarea pertenece al equipo origen (puede ser por ID o nombre)
        const taskTeamId = task.teamId;
        const taskTeamName = teams.find(t => t.id === taskTeamId)?.name;
        const isFromSourceTeam = taskTeamId === bulkSourceTeamId || taskTeamName === bulkSourceTeamId;

        return isFromSourceTeam && task.status === 'pendiente';
      });
      setSelectedTasks(pendingTasks.map(task => task.id));
    } else {
      // En otros casos, seleccionar todas las tareas filtradas
      setSelectedTasks(tasks.map(task => task.id));
    }
  };

  const clearTaskSelection = () => {
    setSelectedTasks([]);
  };

  const handleBulkStep1 = () => {
    if (!bulkSourceTeamId) {
      showNotification('Debe seleccionar un equipo de origen', 'error');
      return;
    }
    setBulkStep(2);
  };

  const handleBulkStep2 = () => {
    if (selectedTasks.length === 0) {
      showNotification('Debe seleccionar al menos una tarea', 'error');
      return;
    }
    setBulkStep(3);
  };

  const handleBulkBack = () => {
    if (bulkStep > 1) {
      setBulkStep(bulkStep - 1);
    }
  };

  const handleBulkCancel = () => {
    setShowBulkModal(false);
    setBulkStep(1);
    setBulkSourceTeamId('');
    setBulkDestTeamId('');
    setSelectedTasks([]);
  };

  const handleExportCsv = async () => {
    try {
      if (!csvDateFrom || !csvDateTo) {
        showNotification('Debe seleccionar el rango de fechas para la exportación', 'error');
        return;
      }

      setExporting(true);

      const blob = await taskService.exportTasksCsv({
        dateFrom: csvDateFrom,
        dateTo: csvDateTo,
        status: csvFilterStatus || undefined,
        teamId: csvFilterTeam || undefined
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tareas_exportadas_${csvDateFrom}_${csvDateTo}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      showNotification('CSV exportado correctamente', 'success');
    } catch (error) {
      console.error('Error exportando tareas:', error);
      showNotification('Error al intentar descargar el CSV', 'error');
    } finally {
      setExporting(false);
    }
  };

  const handleEditTask = (task, startInEditMode = false) => {
    if (!canEditTask(task)) {
      showNotification('El operador solo puede editar tareas pendientes', 'error', 'tasks');
      return;
    }

    setEditTask({
      ...task,
      client: {
        ...task.client,
        secondaryPhone: task.client?.secondaryPhone || ''
      },
      isCorporate: task.isCorporate || false,
      fixedTeam: task.fixedTeam === true,
      estimatedDuration: task.estimatedDuration || '',
      isTimeInHours: task.isTimeInHours !== undefined ? task.isTimeInHours : true,
      ticketNumber: task.ticketNumber || ''
    });

    setOriginalEditTask(task);
    setIsEditing(startInEditMode);
    setTaskModalResetKey((currentKey) => currentKey + 1);
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditTask(null);
    setOriginalEditTask(null);
    setIsEditing(false);
  };

  const handleTaskModalModeChange = (nextMode) => {
    if (nextMode === 'edit') {
      setIsEditing(true);
      return;
    }

    if (originalEditTask) {
      setEditTask({
        ...originalEditTask,
        client: {
          ...originalEditTask.client,
          secondaryPhone: originalEditTask.client?.secondaryPhone || ''
        },
        isCorporate: originalEditTask.isCorporate || false,
        fixedTeam: originalEditTask.fixedTeam === true,
        estimatedDuration: originalEditTask.estimatedDuration || '',
        isTimeInHours: originalEditTask.isTimeInHours !== undefined ? originalEditTask.isTimeInHours : true,
        ticketNumber: originalEditTask.ticketNumber || ''
      });
    }

    setIsEditing(false);
    setTaskModalResetKey((currentKey) => currentKey + 1);
  };

  const handleToggleFixedTeam = async (task) => {
    const isFixed = task.fixedTeam === true;
    try {
      await taskService.updateTask(task.id, buildFixedTeamPayload(task, !isFixed));
      showNotification(isFixed ? 'La tarea se desfijó del equipo' : 'La tarea se fijó al equipo', 'success');
      fetchTasks();
    } catch (error) {
      console.error('Error actualizando equipo fijo:', error);
      showNotification(error.message || 'No se pudo actualizar la tarea', 'error');
    }
  };

  const handleDeleteTaskAction = (task) => {
    if (!canDeleteTask(task)) {
      showNotification('El operador solo puede eliminar tareas pendientes', 'error', 'tasks');
      return;
    }

    setDeleteTaskId(task.id);
    setShowAlertsDeleteTitle('Eliminar tarea');
    setShowAlertsDeleteContenido(`¿Estás seguro de que quieres eliminar la tarea ${task.title}?`);
    setShowAlertsDelete(true);
  };

  const handleUpdateTask = async (forceCreate = false, forceUpdate = false, taskOverride = null) => {
    const taskToUpdate = taskOverride || editTask;
    const validation = validateTask(taskToUpdate, 'edit');
    if (!validation.ok) {
      showNotification(validation.message, 'error', 'tasks');
      return;
    }

    setIsTaskModalSubmitting(true);

    try {
      // Se captura antes: mas abajo setEditTask(null) corre antes del refresco
      const previousTask = tasks.find((task) => task.id === taskToUpdate.id);

      // Preparar los datos para enviar al backend
      const updateData = {
        title: taskToUpdate.title,
        description: taskToUpdate.description,
        priority: taskToUpdate.priority,
        taskType: taskToUpdate.taskType || taskToUpdate.taskTypeId,
        client: {
          name: taskToUpdate.client.name,
          email: taskToUpdate.client.email,
          phone: taskToUpdate.client.phone,
          secondaryPhone: taskToUpdate.client.secondaryPhone,
          cuil: taskToUpdate.client.cuil,
          address: taskToUpdate.client.address,
          lat: taskToUpdate.client.lat,
          lng: taskToUpdate.client.lng
        },
        status: taskToUpdate.status,
        scheduledDate: taskToUpdate.scheduledDate,
        isCorporate: taskToUpdate.isCorporate || false,
        // El toggle solo se muestra con equipo; sin equipo no puede quedar fija
        fixedTeam: Boolean(taskToUpdate.teamId) && taskToUpdate.teamId !== 'null' && taskToUpdate.fixedTeam === true,
        estimatedDuration: taskToUpdate.isCorporate && taskToUpdate.estimatedDuration ? Number(taskToUpdate.estimatedDuration) : null,
        isTimeInHours: taskToUpdate.isCorporate ? taskToUpdate.isTimeInHours : null,
        ticketNumber: taskToUpdate.ticketNumber || '',
        forceCreate: forceCreate || taskToUpdate.forceCreate === true,
        forceUpdate: forceUpdate || taskToUpdate.forceUpdate === true
      };

      // Si hay un teamId, incluirlo
      if (taskToUpdate.teamId) {
        updateData.teamId = taskToUpdate.teamId;
      }

      await taskService.updateTask(taskToUpdate.id, updateData);
      setEditTask(null);
      setForceCreateOrigin(null);
      setPendingForceCreateTask(null);
      setError(null);
      setShowEditModal(false);
      fetchTasks();
      // Las dos fechas: si la edicion movio la tarea de dia, el dia visible pierde una
      refreshTeamLoads(previousTask?.scheduledDate, updateData.scheduledDate);
      showNotification('Tarea actualizada exitosamente', 'success');
    } catch (err) {
      // 409: la tarea esta fijada a su equipo; se pide confirmacion y se reenvia con forceUpdate
      if (err?.status === 409) {
        setShowFixedTeamChangeAlert(true);
      } else if (err?.message?.includes('inhabilitado') && userRole !== 'operator') {
        setShowAlertsForceCreateDate(taskToUpdate.scheduledDate);
        setForceCreateOrigin('edit');
        setShowAlertsForceCreate(true);
      } else {
        console.error('Error actualizando tarea: ', err);
        //setError('Error al actualizar tarea');
        showNotification(err.message, 'error', 'tasks');
      }
    } finally {
      setIsTaskModalSubmitting(false);
    }
  };

  const handleDeleteTask = async () => {
    const taskToDelete = tasks.find(task => task.id === deleteTaskId);
    if (taskToDelete && !canDeleteTask(taskToDelete)) {
      showNotification('El operador solo puede eliminar tareas pendientes', 'error', 'tasks');
      return;
    }

    try {
      await taskService.deleteTask(deleteTaskId);
      fetchTasks();
      refreshTeamLoads(taskToDelete?.scheduledDate);
    } catch (err) {
      setError('Error al eliminar tarea');
    }
  };

  // Función para obtener fechas de inicio y fin según el tipo de filtro
  const getDateRange = (dateType) => {
    const today = new Date();
    const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    switch (dateType) {
      case 'today':
        const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);
        return { start: startOfDay, end: endOfDay };

      case 'week':
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay());
        startOfWeek.setHours(0, 0, 0, 0);
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        endOfWeek.setHours(23, 59, 59);
        return { start: startOfWeek, end: endOfWeek };

      case 'month':
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59);
        return { start: startOfMonth, end: endOfMonth };

      case 'custom':
        if (filterCustomDateFrom && filterCustomDateTo) {
          const startDate = new Date(filterCustomDateFrom);
          const endDate = new Date(filterCustomDateTo);
          startDate.setHours(0, 0, 0, 0);
          endDate.setHours(23, 59, 59, 999);
          return { start: startDate, end: endDate };
        } else if (filterCustomDateFrom) {
          const startDate = new Date(filterCustomDateFrom);
          startDate.setHours(0, 0, 0, 0);
          return { start: startDate, end: null };
        } else if (filterCustomDateTo) {
          const endDate = new Date(filterCustomDateTo);
          endDate.setHours(23, 59, 59, 999);
          return { start: null, end: endDate };
        }
        return { start: startOfDay, end: startOfDay };

      case 'all':
        return { start: null, end: null };

      default:
        return { start: startOfDay, end: startOfDay };
    }
  };

  // Filtrar tareas con filtros avanzados
  // Los filtros se hacen en el backend, así que tasks ya contiene los datos filtrados
  // No usar filter() aquí, el backend maneja todo

  // Columnas de la tabla
  const getTableColumns = () => [
    {
      key: 'status',
      label: 'Estado',
      align: 'center',
      render: (task) => (
        <span className={`inline-flex items-center px-1 py-0.5 rounded-full text-xs font-medium ${task.status === 'pendiente' ? 'bg-st-pendiente/12 text-st-pendiente border border-st-pendiente' :
          task.status === 'finalizada' ? 'bg-st-finalizada/12 text-st-finalizada border border-st-finalizada' :
            task.status === 'iniciada' ? 'bg-st-en-camino/12 text-st-en-camino border border-st-en-camino' :
              task.status === 'en_camino' ? 'bg-st-en-camino/12 text-st-en-camino border border-st-en-camino' :
                task.status === 'cancelada' ? 'bg-st-cancelada/12 text-st-cancelada border border-st-cancelada' :
                  'bg-surface-2 text-content border border-line'
          }`}>
          {task.status || 'pendiente'}
        </span>
      )
    },
    {
      key: 'scheduledDate',
      label: 'Fecha',
      align: 'center',
      render: (task) => (
        task.scheduledDate ? new Date(task.scheduledDate).toLocaleString('es-ES', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        }) : '-'
      )
    },
    {
      key: 'title',
      label: 'Título',
      align: 'center',
      mobilePrimary: true,
      render: (task) => {
        const fullTitle = task.title?.trim();

        if (!fullTitle) return '-';

        const isTruncated = fullTitle.length > 20;
        const truncatedTitle = isTruncated
          ? `${fullTitle.slice(0, 19)}…`
          : fullTitle;

        return (
          <Tooltip
            content={fullTitle}
            placement="right"
            tabIndex={0}
            disableOnMobile
          >
            <span aria-label={fullTitle}>{truncatedTitle}</span>
          </Tooltip>
        );
      }
    },
    {
      key: 'client',
      label: 'Cliente',
      align: 'center',
      render: (task) => task.client?.name || task.client?.email || '-'
    },
    {
      key: 'type',
      label: 'Tipo',
      align: 'center',
      render: (task) => taskTypes.find(t => t.name === task.taskTypeId)?.displayName || task.taskType
    },
    ...(currentUserRole === 'admin' ? [
      {
        key: 'validation',
        label: 'Validación',
        align: 'center',
        width: '10%',
        render: (task) => {
          const isValidated = task.isValidated === true;
          const validateReason = task?.validateReason;
          const hasValidationReason = (() => {
            if (!validateReason) {
              return false;
            }

            if (typeof validateReason === 'string') {
              return validateReason.trim().length > 0;
            }

            if (typeof validateReason === 'object') {
              return Object.values(validateReason).some((value) => {
                if (typeof value === 'string') {
                  return value.trim().length > 0;
                }

                return value !== null && value !== undefined;
              });
            }

            return Boolean(validateReason);
          })();

          const iconMeta = !isValidated
            ? {
              Icon: Clock,
              title: 'Pendiente',
              className: 'bg-yellow-900/20 text-yellow-200 border border-yellow-600'
            }
            : hasValidationReason
              ? {
                Icon: Checks,
                title: 'Validada con motivo',
                className: 'bg-emerald-900/20 text-emerald-200 border border-emerald-600'
              }
              : {
                Icon: Check,
                title: 'Validada automática',
                className: 'bg-green-900/20 text-green-200 border border-green-600'
              };

          const ValidationIcon = iconMeta.Icon;

          return (
            <span
              className={`inline-flex items-center justify-center rounded-full p-1.5 ${iconMeta.className}`}
              title={iconMeta.title}
              aria-label={iconMeta.title}
            >
              <ValidationIcon size={14} weight="bold" />
            </span>
          );
        }
      }
    ] : []),
    ...(filters.status === 'cancelada' || (Array.isArray(filters.status) && filters.status.includes('cancelada')) ? [
      {
        key: 'requiresReschedule',
        label: 'Reagenda',
        align: 'center',
        render: (task) => task.requiresReschedule ? 'Sí' : 'No'
      },
      {
        key: 'hasVisit',
        label: 'Visita',
        align: 'center',
        render: (task) => task.hasVisit ? 'Con visita' : 'Sin visita'
      }
    ] : []),
    {
      key: 'reassign',
      label: 'Reasignar',
      align: 'center',
      width: '14%',
      render: (task) => (
        <select
          value={task.teamId || ''}
          onChange={(e) => handleIndividualReassign(task.id, e.target.value)}
          className="w-full px-3 py-2.5 text-sm border border-line rounded-lg bg-bg text-content shadow-sm focus:outline-none focus:ring-2 focus:ring-amber focus:border-amber"
        >
          <option value="">Sin equipo</option>
          {teams.map(team => (
            <option key={team.id} value={team.id}>
              {team.name}
            </option>
          ))}
        </select>
      )
    },
    {
      key: 'acciones',
      label: 'Acciones',
      align: 'center',
      isAction: true,
      render: (task) => (
        <span data-tour="task-row-actions" className="inline-flex">
          <ActionMenu
            options={[
              {
                label: 'Ver',
                icon: <Eye size={16} />,
                onClick: () => handleViewTask(task)
              },
              ...(canEditTask(task) ? [{
                label: 'Editar',
                icon: <PencilSimple size={16} />,
                onClick: () => handleEditTask(task, true)
              }] : []),
              ...(taskHasTeam(task) ? [{
                label: task.fixedTeam === true ? 'Desfijar' : 'Fijar',
                icon: task.fixedTeam === true ? <PushPinSlash size={16} /> : <PushPin size={16} />,
                onClick: () => handleToggleFixedTeam(task)
              }] : []),
              ...(canDeleteTask(task) ? [{
                label: 'Eliminar',
                icon: <Trash size={16} />,
                onClick: () => handleDeleteTaskAction(task)
              }] : [])
            ]}
          />
        </span>
      )
    }
  ];

  const handleViewTask = (task) => {
    if (typeof onViewTask !== 'function') {
      return;
    }

    const taskId = task?.id ?? task?.taskId ?? task?._id;
    if (!taskId) {
      showNotification('No se pudo abrir la tarea seleccionada', 'error', 'tasks');
      return;
    }

    // Forzar transición para cubrir el caso de reabrir la misma tarea.
    onViewTask(null);
    setTimeout(() => onViewTask(taskId), 0);
  };

  // Lógica de paginación: como el backend maneja paginación, no necesitamos slice
  // Los datos en 'tasks' ya son la página actual
  const paginatedTasks = tasks;

  // Disparar fetchTasks cuando cambien los filtros o paginación
  useEffect(() => {
    fetchTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, pageSize, currentPage, viewMode]);

  // Calcular estadísticas
  const getStats = () => {
    const today = new Date();
    const todayStr = today.toISOString().slice(0, 10);

    const todayTasks = tasks.filter(task => {
      const taskDate = task.scheduledDate ? task.scheduledDate.slice(0, 10) : '';
      return taskDate === todayStr;
    });

    const weekTasks = tasks.filter(task => {
      const taskDate = task.scheduledDate ? new Date(task.scheduledDate) : null;
      if (!taskDate) return false;
      const { start, end } = getDateRange('week');
      return taskDate >= start && taskDate <= end;
    });

    const monthTasks = tasks.filter(task => {
      const taskDate = task.scheduledDate ? new Date(task.scheduledDate) : null;
      if (!taskDate) return false;
      const { start, end } = getDateRange('month');
      return taskDate >= start && taskDate <= end;
    });

    return {
      today: todayTasks.length,
      week: weekTasks.length,
      month: monthTasks.length,
      total: tasks.length
    };
  };

  const stats = getStats();
  const taskModalSections = useMemo(
    () => buildTaskModalSections({
      taskTypes,
      teams,
      loadingTaskTypes,
      normalizeCoordinates,
      showFixedTeamForTour: tourStep?.modal === 'task-create'
    }),
    [taskTypes, teams, loadingTaskTypes, tourStep?.modal]
  );

  return (
    <div>
      {/* Notificación emergente */}
      {notification.origin !== 'tasks' && notification.origin !== 'reassignment' && notification.show && (
        <div className={`fixed top-4 right-4 z-[9999] p-4 rounded-lg shadow-lg border-l-4 transition-all duration-300 ${notification.type === 'success'
          ? 'bg-green-900/20 border-green-600 text-green-300'
          : 'bg-red-900/20 border-red-600 text-red-300'
          }`}>
          <div className="flex items-center space-x-3">
            <div className={`text-xl ${notification.type === 'success' ? 'text-green-400' : 'text-red-400'
              }`}>
              {notification.type === 'success' ? '✅' : '❌'}
            </div>
            <div>
              <p className="font-medium">{notification.message}</p>
            </div>
          </div>
        </div>
      )}

      <PageHeader
        title="Gestión de Tareas"
        subtitle="Administra y crea tareas para los técnicos."
        icon={<FileText size={28} />}
        actions={(
          <div className="flex flex-wrap items-center gap-3">
            <div data-tour="task-view-toggle" className="flex items-center bg-surface-2 border border-line rounded-xl p-1">
              <button
                type="button"
                onClick={() => handleTaskViewModeChange('calendar')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${viewMode === 'calendar'
                  ? 'bg-amber text-on-amber'
                  : 'text-content-muted hover:text-content'
                  }`}
              >
                <CalendarBlank size={16} />
                Calendario
              </button>
              <button
                type="button"
                onClick={() => handleTaskViewModeChange('list')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${viewMode === 'list'
                  ? 'bg-amber text-on-amber'
                  : 'text-content-muted hover:text-content'
                  }`}
              >
                <ListBullets size={16} />
                Lista
              </button>
            </div>

            {(userRole === 'admin' || userRole === 'operator') && (
              <button
                data-tour="task-create"
                onClick={handleCreateForm}
                className="self-start bg-amber hover:bg-amber-hover text-on-amber px-4 py-2 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 flex items-center gap-2 font-semibold text-sm sm:self-auto"
              >
                <span className="text-lg"><Plus size={20} /></span>
                Crear Nueva Tarea
              </button>
            )}
          </div>
        )}
      />

      {error && (
        <div className="mb-4 rounded-lg border border-red-600 bg-red-900/20 p-3 text-sm text-red-300">
          {error}
        </div>
      )}

      <div className="flex justify-end mb-2" style={{ position: 'relative' }}>

        {showClientCriteriaSettings && (
          <div className="absolute right-0 top-full mt-1 z-[1000] w-72 rounded-lg border border-line bg-surface p-3 shadow-2xl">
            <p className="text-xs font-medium text-content mb-2">
              Criterios visibles y orden
            </p>
            <ul className="space-y-1 mb-3">
              {clientCriteriaOrder
                .map(value => CLIENT_SEARCH_CRITERIA.find(c => c.value === value))
                .filter(Boolean)
                .map((criterion, index, arr) => {
                  const isVisible = clientCriteriaVisible.includes(criterion.value);
                  const isLastVisible = isVisible && clientCriteriaVisible.length === 1;

                  const toggleVisible = () => {
                    if (isVisible) {
                      if (isLastVisible) return;
                      setClientCriteriaVisible(clientCriteriaVisible.filter(v => v !== criterion.value));
                    } else {
                      setClientCriteriaVisible([...clientCriteriaVisible, criterion.value]);
                    }
                  };

                  const moveBy = (offset) => {
                    const targetIndex = index + offset;
                    if (targetIndex < 0 || targetIndex >= arr.length) return;
                    const nextOrder = arr.map(c => c.value);
                    const temp = nextOrder[index];
                    nextOrder[index] = nextOrder[targetIndex];
                    nextOrder[targetIndex] = temp;
                    setClientCriteriaOrder(nextOrder);
                  };

                  return (
                    <li key={criterion.value} className="flex items-center justify-between gap-2 text-sm text-content">
                      <label className="flex items-center gap-2 flex-1 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isVisible}
                          disabled={isLastVisible}
                          onChange={toggleVisible}
                        />
                        {criterion.label}
                      </label>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => moveBy(-1)}
                          disabled={index === 0}
                          className="p-1 text-content-muted hover:text-content disabled:opacity-30"
                          title="Subir"
                        >
                          <ArrowUp size={12} />
                        </button>
                        <button
                          type="button"
                          onClick={() => moveBy(1)}
                          disabled={index === arr.length - 1}
                          className="p-1 text-content-muted hover:text-content disabled:opacity-30"
                          title="Bajar"
                        >
                          <ArrowDown size={12} />
                        </button>
                      </div>
                    </li>
                  );
                })}
            </ul>
            <button
              type="button"
              onClick={resetClientCriteriaPreferences}
              className="text-xs text-accent-text hover:text-amber-hover"
            >
              Restaurar valores por defecto
            </button>
          </div>
        )}
      </div>

      <div data-tour="task-filters">
        <GenericFilters
          columns={[
            {
              type: 'select',
              key: 'status',
              label: 'Estado',
              options: [
                { value: '', label: 'Todos' },
                { value: 'pendiente', label: 'Pendiente' },
                { value: 'en_camino', label: 'En camino' },
                { value: 'iniciada', label: 'Iniciada' },
                { value: 'finalizada', label: 'Finalizada' },
                { value: 'cancelada', label: 'Cancelada' }
              ],
              optionValue: 'value',
              optionLabel: 'label',
              colSpan: 1
            },
            {
              type: 'select',
              key: 'dateType',
              label: 'Fecha',
              options: [
                { value: 'today', label: 'Hoy' },
                { value: 'week', label: 'Semana' },
                { value: 'month', label: 'Mes' },
                { value: 'custom', label: 'Período' },
                { value: 'all', label: 'Todas' }
              ],
              optionValue: 'value',
              optionLabel: 'label',
              colSpan: 1,
              showWhen: () => viewMode !== 'calendar'
            },
            {
              type: 'dateRange',
              key: 'customDateRange',
              label: 'Período',
              startKey: 'customDateFrom',
              endKey: 'customDateTo',
              popupPlacement: 'bottom',
              colSpan: 2,
              showWhen: (currentFilters) => viewMode !== 'calendar' && currentFilters.dateType === 'custom'
            },
            {
              type: 'criteriaSearch',
              key: 'clientSearch',
              criteriaKey: 'clientSearchCriteria',
              valueKey: 'clientSearchValue',
              label: 'Buscar por',
              criteriaOptions: orderedVisibleCriteria,
              colSpan: 2
            },
            {
              type: 'select',
              key: 'type',
              label: 'Tipo',
              options: [
                { value: '', label: 'Todos' },
                ...taskTypes.map(taskType => ({ value: taskType.name, label: taskType.displayName }))
              ],
              optionValue: 'value',
              optionLabel: 'label',
              colSpan: 1
            },
            {
              type: 'select',
              key: 'priority',
              label: 'Prioridad',
              options: [
                { value: '', label: 'Todas' },
                { value: 'baja', label: 'Baja' },
                { value: 'media', label: 'Media' },
                { value: 'alta', label: 'Alta' }
              ],
              optionValue: 'value',
              optionLabel: 'label',
              colSpan: 1
            },
            {
              type: 'select',
              key: 'team',
              label: 'Equipo',
              options: [
                { value: '', label: 'Todos' },
                ...teams.map(team => ({ value: team.id, label: team.name }))
              ],
              optionValue: 'value',
              optionLabel: 'label',
              colSpan: 1
            },
            ...(currentUserRole === 'admin' ? [
              {
                type: 'select',
                key: 'validation',
                label: 'Validación',
                options: [
                  { value: '', label: 'Todas' },
                  { value: 'false', label: 'Pendiente' },
                  { value: 'true', label: 'Validada' }
                ],
                optionValue: 'value',
                optionLabel: 'label',
                colSpan: 1
              }
            ] : []),
            {
              type: 'select',
              key: 'requiresReschedule',
              label: 'Requiere Reagenda',
              options: [
                { value: 'all', label: 'Todas' },
                { value: 'true', label: 'Sí' },
                { value: 'false', label: 'No' }
              ],
              optionValue: 'value',
              optionLabel: 'label',
              colSpan: 1,
              showWhen: (f) => f.status === 'cancelada' || (Array.isArray(f.status) && f.status.includes('cancelada'))
            },
            {
              type: 'select',
              key: 'hasVisit',
              label: 'Visita',
              options: [
                { value: 'all', label: 'Todas' },
                { value: 'true', label: 'Con visita' },
                { value: 'false', label: 'Sin visita' }
              ],
              optionValue: 'value',
              optionLabel: 'label',
              colSpan: 1,
              showWhen: (f) => f.status === 'cancelada' || (Array.isArray(f.status) && f.status.includes('cancelada'))
            }
          ]}
          filters={filters}
          onFilterChange={(newFilters) => {
            const normalizedFilters = { ...newFilters };

            if (normalizedFilters.dateType === 'today') {
              normalizedFilters.date = formatFilterDate(new Date());
              normalizedFilters.customDateFrom = '';
              normalizedFilters.customDateTo = '';
            }

            if (normalizedFilters.dateType === 'month') {
              normalizedFilters.date = normalizedFilters.date || formatFilterDate(new Date());
            }

            if (normalizedFilters.dateType === 'week' || normalizedFilters.dateType === 'all') {
              normalizedFilters.customDateFrom = '';
              normalizedFilters.customDateTo = '';
            }

            if (
              normalizedFilters.dateType === 'custom'
              && normalizedFilters.customDateFrom
              && normalizedFilters.customDateFrom === normalizedFilters.customDateTo
            ) {
              normalizedFilters.date = normalizedFilters.customDateFrom;
            }

            setFilters(normalizedFilters);
            setCurrentPage(1);
          }}
          onClearFilters={() => {
            const defaultFilters = buildDefaultTaskFilters();
            setFilters(defaultFilters);
            setCurrentPage(1);
          }}
          key={tourStep?.openFilters ? 'tour' : 'filters'}
          defaultOpen={Boolean(tourStep?.openFilters)}
        />
      </div>

      {/* Modal genérico para crear tareas */}
      <ModalCreationEditing
        isOpen={showCreateModal}
        mode="create"
        icon={<Plus size={20} />}
        title="Crear nueva tarea"
        submitText="Crear tarea"
        sections={taskModalSections}
        initialValues={toTaskModalValues(newTask)}
        initialValuesKey={`create-${taskModalResetKey}`}
        notification={notification.origin === 'tasks' ? notification : null}
        onValuesChange={(values) => setNewTask(fromTaskModalValues(values))}
        onSubmit={(values) => handleAddTask(false, fromTaskModalValues(values))}
        onClose={handleCloseCreateModal}
        isSubmitting={isTaskModalSubmitting}
      />

      {/* El mismo modal genérico permite visualizar y editar tareas */}
      {showEditModal && editTask && (
        <ModalCreationEditing
          isOpen={showEditModal}
          mode={isEditing ? 'edit' : 'view'}
          icon={isEditing ? <Pencil size={20} /> : <Eye size={20} />}
          title={isEditing ? 'Editar tarea' : 'Ver tarea'}
          submitText="Guardar cambios"
          sections={taskModalSections}
          initialValues={toTaskModalValues(editTask)}
          initialValuesKey={`edit-${taskModalResetKey}`}
          notification={notification.origin === 'tasks' ? notification : null}
          onValuesChange={(values) => setEditTask((currentTask) => ({
            ...currentTask,
            ...fromTaskModalValues(values)
          }))}
          onSubmit={(values) => handleUpdateTask(false, false, {
            ...editTask,
            ...fromTaskModalValues(values)
          })}
          onClose={handleCloseEditModal}
          onModeChange={handleTaskModalModeChange}
          isSubmitting={isTaskModalSubmitting}
          viewModeToggleText="Cambiar a Edición"
          editModeToggleText="Cambiar a modo Visualizar"
        />
      )}

      {/* Tabla de Tareas */}
      {viewMode === 'calendar' ? (
        <TasksCalendarView
          tasks={tasks}
          teams={teams}
          taskTypes={taskTypes}
          teamLoadsRefreshToken={teamLoadsRefreshToken}
          selectedTeamId={filters.team || ''}
          showValidation={currentUserRole !== 'operator'}
          mode={calendarMode}
          selectedDate={calendarDate}
          onModeChange={(nextMode, nextDate) => syncCalendarFilters(nextMode, nextDate || calendarDate)}
          onDateChange={(nextDate) => syncCalendarFilters(calendarMode, nextDate)}
          loading={loading}
          onViewTask={handleViewTask}
          onEditTask={handleEditTask}
          onDeleteTask={handleDeleteTaskAction}
          canEditTask={canEditTask}
          canDeleteTask={canDeleteTask}
          onToggleFixedTeam={handleToggleFixedTeam}
        />
      ) : (
        <div data-tour="task-list" className="relative task-section md:overflow-hidden md:rounded-xl md:border md:border-line md:bg-surface md:shadow-md">
          <GenericTables
            columns={getTableColumns()}
            data={paginatedTasks}
            emptyMessage="No hay tareas disponibles"
            zebraStripe={true}
            showFooter={true}
            pagination={{
              currentPage,
              totalPages,
              pageSize,
              totalItems: totalResponses,
              pageSizeOptions: [5, 10, 20, 50],
              onPageChange: setCurrentPage,
              onPageSizeChange: (newSize) => {
                setPageSize(newSize);
                setCurrentPage(1);
              }
            }}
          />
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-bg/60">
              <div className="flex items-center gap-3 rounded-lg border border-line bg-surface/90 px-4 py-3 text-sm text-content shadow-lg">
                <span className="h-5 w-5 animate-spin rounded-full border-2 border-amber border-t-transparent" />
                Consultando tareas...
              </div>
            </div>
          )}
        </div>
      )}

      {/* Botones de Acción */}
      <div className="flex gap-3 mt-4 task-filters">
        <button
          onClick={() => setShowBulkModal(true)}
          className="bg-surface-2 hover:bg-line disabled:bg-surface-2 text-content px-6 py-3 rounded-lg flex items-center gap-2 font-medium transition-colors"
        >
          <span><ArrowsClockwise size={16} /></span>
          Reasignación Masiva
        </button>

        <TintedButton onClick={() => setShowCsvModal(true)}>
          <span><ChartBar size={16} /></span>
          Exportar CSV
        </TintedButton>
      </div>
      {/* Modal de Reasignación Masiva - 3 Pasos */}
      {showBulkModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-9998 p-4">
          <div className="bg-surface rounded-lg shadow-xl border border-line max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header del modal */}
            <div className="flex items-center justify-between p-6 border-b border-line">
              <div className="flex items-center gap-3">
                <div className="text-3xl"><ArrowsClockwise size={20} /></div>
                <h2 className="text-2xl font-bold text-content">Reasignación Masiva por Equipo</h2>
              </div>
              <button
                onClick={handleBulkCancel}
                className="text-content hover:text-content text-2xl"
              >
                ✕
              </button>
            </div>

            {/* Indicador de pasos */}
            <div className="p-6 border-b border-line">
              <div className="flex items-center justify-center space-x-4">
                <div className={`flex items-center space-x-2 ${bulkStep >= 1 ? 'text-accent-text' : 'text-content-muted'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${bulkStep >= 1 ? 'bg-amber' : 'bg-surface-2'}`}>
                    1
                  </div>
                  <span className="text-sm font-medium">Equipo Origen</span>
                </div>
                <div className={`w-8 h-0.5 ${bulkStep >= 2 ? 'bg-amber' : 'bg-surface-2'}`}></div>
                <div className={`flex items-center space-x-2 ${bulkStep >= 2 ? 'text-accent-text' : 'text-content-muted'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${bulkStep >= 2 ? 'bg-amber' : 'bg-surface-2'}`}>
                    2
                  </div>
                  <span className="text-sm font-medium">Tareas Pendientes</span>
                </div>
                <div className={`w-8 h-0.5 ${bulkStep >= 3 ? 'bg-amber' : 'bg-surface-2'}`}></div>
                <div className={`flex items-center space-x-2 ${bulkStep >= 3 ? 'text-accent-text' : 'text-content-muted'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${bulkStep >= 3 ? 'bg-amber' : 'bg-surface-2'}`}>
                    3
                  </div>
                  <span className="text-sm font-medium">Equipo Destino</span>
                </div>
              </div>
            </div>

            {/* Contenido del modal */}
            <div className="p-6">
              {/* Paso 1: Seleccionar equipo origen */}
              {bulkStep === 1 && (
                <div>
                  <h3 className="text-lg font-semibold text-content mb-4">Paso 1: Seleccionar Equipo de Origen</h3>
                  <p className="text-sm text-content mb-6">Selecciona el equipo del que quieres reasignar tareas</p>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-content mb-2">Equipo de origen</label>
                    <select
                      className="w-full px-3 py-2 border border-line rounded-md bg-surface-2 text-content focus:outline-none focus:ring-2 focus:ring-amber"
                      value={bulkSourceTeamId}
                      onChange={(e) => {
                        setBulkSourceTeamId(e.target.value);
                        const selectedTeam = teams.find(team => team.id === e.target.value);
                        setBulkSourceTeamName(selectedTeam ? selectedTeam.name : '');
                      }}
                    >
                      <option value="">Seleccionar equipo</option>
                      {teams.map(team => (
                        <option key={team.id} value={team.id}>
                          {team.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="flex justify-end">
                    <button
                      onClick={handleBulkStep1}
                      disabled={!bulkSourceTeamId}
                      className={`px-6 py-3 rounded-lg font-semibold transition-colors ${!bulkSourceTeamId
                        ? 'cursor-not-allowed bg-surface-2 text-content-muted'
                        : 'bg-amber text-on-amber hover:bg-amber-hover'
                        }`}
                    >
                      Continuar
                    </button>
                  </div>
                </div>
              )}

              {/* Paso 2: Seleccionar tareas pendientes */}
              {bulkStep === 2 && (
                <div>
                  <h3 className="text-lg font-semibold text-content mb-4">Paso 2: Seleccionar Tareas Pendientes</h3>
                  <p className="text-sm text-content mb-6">Selecciona las tareas en estado "pendiente" del {bulkSourceTeamName}</p>

                  <div className="mb-4 flex items-center space-x-4">
                    <button
                      onClick={selectAllTasks}
                      className="px-3 py-2 text-sm bg-amber text-on-amber rounded hover:bg-amber-hover"
                    >
                      Seleccionar todas
                    </button>
                    <button
                      onClick={clearTaskSelection}
                      className="px-3 py-2 text-sm bg-surface-2 text-content rounded hover:bg-line"
                    >
                      Limpiar
                    </button>
                    <span className="text-sm text-content">
                      <FileText size={20} /> {selectedTasks.length} tareas seleccionadas
                    </span>
                  </div>

                  <div className="bg-surface-2 rounded-lg p-4 mb-6">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-line">
                            <th className="px-3 py-2 text-left text-sm font-medium text-content">
                              <input
                                type="checkbox"
                                onChange={(e) => e.target.checked ? selectAllTasks() : clearTaskSelection()}
                                checked={selectedTasks.length === tasks.filter(t => {
                                  const taskTeamId = t.teamId;
                                  const taskTeamName = teams.find(team => team.id === taskTeamId)?.name;
                                  const isFromSourceTeam = taskTeamId === bulkSourceTeamId || taskTeamName === bulkSourceTeamId;
                                  return isFromSourceTeam && t.status === 'pendiente';
                                }).length && tasks.filter(t => {
                                  const taskTeamId = t.teamId;
                                  const taskTeamName = teams.find(team => team.id === taskTeamId)?.name;
                                  const isFromSourceTeam = taskTeamId === bulkSourceTeamId || taskTeamName === bulkSourceTeamId;
                                  return isFromSourceTeam && t.status === 'pendiente';
                                }).length > 0}
                                className="form-checkbox h-4 w-4 text-amber"
                              />
                            </th>
                            <th className="px-3 py-2 text-left text-sm font-medium text-content">Título</th>
                            <th className="px-3 py-2 text-left text-sm font-medium text-content">Cliente</th>
                            <th className="px-3 py-2 text-left text-sm font-medium text-content">Estado</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-line">
                          {tasks
                            .filter(task => {
                              // Verificar si la tarea pertenece al equipo origen (puede ser por ID o nombre)
                              const taskTeamId = task.teamId;
                              const taskTeamName = teams.find(t => t.id === taskTeamId)?.name;
                              const isFromSourceTeam = taskTeamId === bulkSourceTeamId || taskTeamName === bulkSourceTeamId;

                              return isFromSourceTeam && task.status === 'pendiente';
                            })
                            .slice(0, 10)
                            .map((task) => (
                              <tr key={task.id} className="hover:bg-surface-2">
                                <td className="px-3 py-2">
                                  <input
                                    type="checkbox"
                                    checked={selectedTasks.includes(task.id)}
                                    onChange={() => toggleTaskSelection(task.id)}
                                    className="form-checkbox h-4 w-4 text-amber"
                                  />
                                </td>
                                <td className="px-3 py-2 text-sm text-content">{task.title}</td>
                                <td className="px-3 py-2 text-sm text-content">
                                  {task.client?.name || task.client?.email}
                                </td>
                                <td className="px-3 py-2 text-sm">
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-900/20 text-yellow-200 border border-yellow-600">
                                    {task.status}
                                  </span>
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="flex justify-between">
                    <button
                      onClick={handleBulkBack}
                      className="px-6 py-3 bg-surface-2 text-content rounded-lg hover:bg-line transition-colors"
                    >
                      Atrás
                    </button>
                    <button
                      onClick={handleBulkStep2}
                      disabled={selectedTasks.length === 0}
                      className={`px-6 py-3 rounded-lg font-semibold transition-colors ${selectedTasks.length === 0
                        ? 'cursor-not-allowed bg-surface-2 text-content-muted'
                        : 'bg-amber text-on-amber hover:bg-amber-hover'
                        }`}
                    >
                      Continuar
                    </button>
                  </div>
                </div>
              )}

              {/* Paso 3: Seleccionar equipo destino */}
              {bulkStep === 3 && (
                <div>
                  {notification.origin === "reassignment" && notification.show && (
                    <div className={`mb-4 p-4 rounded-lg ${notification.type === 'success' ? 'bg-green-900/20 border border-green-600 text-green-400' : 'bg-red-900/20 border border-red-600 text-red-400'}`}>
                      {notification.message}
                    </div>
                  )}
                  <h3 className="text-lg font-semibold text-content mb-4">Paso 3: Seleccionar Equipo Destino</h3>
                  <p className="text-sm text-content mb-6">Selecciona el equipo destino para las {selectedTasks.length} tareas seleccionadas</p>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-content mb-2">Equipo destino</label>
                    <select
                      className="w-full px-3 py-2 border border-line rounded-md bg-surface-2 text-content focus:outline-none focus:ring-2 focus:ring-amber"
                      value={bulkDestTeamId}
                      onChange={(e) => {
                        setBulkDestTeamId(e.target.value);
                        const selectedTeam = teams.find(team => team.id === e.target.value);
                        setBulkDestTeamName(selectedTeam ? selectedTeam.name : '');
                      }}
                    >
                      <option value="">Seleccionar equipo</option>
                      {teams
                        .filter(team => team.name !== bulkSourceTeamId)
                        .map(team => (
                          <option key={team.id} value={team.id}>
                            {team.name}
                          </option>
                        ))}
                    </select>
                  </div>

                  <div className="bg-info/12 border border-info rounded-lg p-4 mb-6">
                    <h4 className="text-sm font-medium text-info mb-2">Resumen de la operación:</h4>
                    <div className="text-sm text-info">
                      <p>• Equipo origen: <span className="font-medium">{bulkSourceTeamName}</span></p>
                      <p>• Tareas a reasignar: <span className="font-medium">{selectedTasks.length}</span></p>
                      <p>• Equipo destino: <span className="font-medium">{bulkDestTeamName || 'Por seleccionar'}</span></p>
                    </div>
                  </div>

                  <div className="flex justify-between">
                    <button
                      onClick={handleBulkBack}
                      className="px-6 py-3 bg-surface-2 text-content rounded-lg hover:bg-line transition-colors"
                    >
                      Atrás
                    </button>
                    <button
                      onClick={() => handleBulkAssign()}
                      disabled={bulkLoading || !bulkDestTeamId}
                      className={`px-6 py-3 rounded-lg font-semibold transition-colors ${bulkLoading || !bulkDestTeamId
                        ? 'cursor-not-allowed bg-surface-2 text-content-muted'
                        : 'bg-success/16 hover:bg-success/24 border border-success/60 text-success'
                        }`}
                    >
                      {bulkLoading ? 'Procesando…' : `Reasignar ${selectedTasks.length} tareas`}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Exportación CSV */}
      {showCsvModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-9998 p-4">
          <div className="bg-surface rounded-lg shadow-xl border border-line max-w-2xl w-full">
            {/* Header del modal */}
            <div className="flex items-center justify-between p-6 border-b border-line">
              <div className="flex items-center gap-3">
                <div className="text-3xl"><ChartBar size={20} /></div>
                <h2 className="text-2xl font-bold text-content">Exportar CSV</h2>
              </div>
              <button
                onClick={() => setShowCsvModal(false)}
                className="text-content hover:text-content text-2xl"
              >
                ✕
              </button>
            </div>

            {/* Contenido del modal */}
            <div className="p-6">
              <p className="text-sm text-content mb-6">Configure los filtros para exportar tareas específicas</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-content mb-2">Desde</label>
                  <input
                    type="date"
                    className="w-full px-3 py-2 border border-line rounded-md bg-surface-2 text-content focus:outline-none focus:ring-2 focus:ring-amber"
                    value={csvDateFrom}
                    onChange={(e) => setCsvDateFrom(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-content mb-2">Hasta</label>
                  <input
                    type="date"
                    className="w-full px-3 py-2 border border-line rounded-md bg-surface-2 text-content focus:outline-none focus:ring-2 focus:ring-amber"
                    value={csvDateTo}
                    onChange={(e) => setCsvDateTo(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-content mb-2">Estado</label>
                  <select
                    className="w-full px-3 py-2 border border-line rounded-md bg-surface-2 text-content focus:outline-none focus:ring-2 focus:ring-amber"
                    value={csvFilterStatus}
                    onChange={(e) => setCsvFilterStatus(e.target.value)}
                  >
                    <option value="">Todos los estados</option>
                    <option value="pendiente">Pendiente</option>
                    <option value="en_camino">En camino</option>
                    <option value="iniciada">Iniciada</option>
                    <option value="finalizada">Finalizada</option>
                    <option value="cancelada">Cancelada</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-content mb-2">Equipo</label>
                  <select
                    className="w-full px-3 py-2 border border-line rounded-md bg-surface-2 text-content focus:outline-none focus:ring-2 focus:ring-amber"
                    value={csvFilterTeam}
                    onChange={(e) => setCsvFilterTeam(e.target.value)}
                  >
                    <option value="">Todos los equipos</option>
                    {teams.map(team => (
                      <option key={team.id} value={team.id}>{team.name}</option>
                    ))}
                  </select>
                </div>
              </div>



              {/* Botones de acción */}
              <div className="flex justify-end gap-4 pt-6 border-t border-line">
                <button
                  onClick={() => setShowCsvModal(false)}
                  className="px-6 py-3 bg-surface-2 text-content rounded-lg hover:bg-line transition-colors"
                >
                  Cancelar
                </button>
                <TintedButton
                  onClick={() => {
                    handleExportCsv();
                    setShowCsvModal(false);
                  }}
                  disabled={exporting || !csvDateFrom || !csvDateTo}
                >
                  {exporting ? 'Exportando…' : 'Exportar CSV'}
                </TintedButton>
              </div>
            </div>
          </div>
        </div>
      )}
      {showAlerts && (
        <Alerts title={"Aviso"} onClose={() => setshowAlerts(false)} width='max-w-md'>
          <h1 className="text-lg font-bold mb-4 m-6 text-center">{showAlertsContenido}</h1>
        </Alerts>
      )}
      {showAlertsDelete && (
        <Alerts title={showAlertsDeleteTitle} onClose={() => setShowAlertsDelete(false)} width='max-w-md'>
          <h2 className="text-lg font-semibold">{showAlertsDeleteContenido}</h2>

          {/* Buttons */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-line">
            <button
              type="button"
              onClick={() => {
                setShowAlertsDelete(false);
                setDeleteTaskId(null);
              }}
              className="px-4 py-2 text-content bg-surface-2 rounded-md hover:bg-line"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600"
              onClick={() => { handleDeleteTask(); setShowAlertsDelete(false); }}
            >
              Eliminar
            </button>
          </div>

        </Alerts>
      )}
      {showAlertsForceCreate && (
        <Alerts title={"Aviso"} onClose={() => setShowAlertsForceCreate(false)} width='max-w-md'>
          <h3 className="text-md ">El equipo seleccionado se encuentra inhabilitado para la fecha: {formatFilterDate(showAlertsForceCreateDate)} ¿Desea continuar con la asignación e ignorar esta inhabilitación?</h3>
          
          <div className="flex justify-end space-x-3 pt-6 border-t border-line">
            <button
              type="button"
              onClick={() => {
                setShowAlertsForceCreate(false);
                setShowAlertsForceCreateDate('');
                setForceCreateOrigin(null);
                setPendingReassignTaskId(null);
                setPendingReassignTeamId('');
              }}
              className="px-4 py-2 text-content bg-surface-2 rounded-md hover:bg-line"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-success/16 hover:bg-success/24 border border-success/60 text-success rounded-md transition-colors"
              onClick={() => {
                setShowAlertsForceCreate(false);
                if (forceCreateOrigin === 'bulk') {
                  handleBulkAssign(true);
                  return;
                }

                if (forceCreateOrigin === 'reassign' && pendingReassignTaskId) {
                  handleIndividualReassign(
                    pendingReassignTaskId,
                    pendingReassignTeamId,
                    true
                  );
                  return;
                }

                if (forceCreateOrigin === 'create') {
                  handleAddTask(true, pendingForceCreateTask);
                  return;
                }

                setEditTask((previousTask) => previousTask ? ({ ...previousTask, forceCreate: true }) : previousTask);
                handleUpdateTask(true);
              }}
            >
              {forceCreateOrigin === 'create'
                ? 'Continuar creación'
                : forceCreateOrigin === 'bulk'
                  ? 'Continuar reasignación'
                  : forceCreateOrigin === 'reassign'
                    ? 'Confirmar cambio'
                  : 'Continuar asignación'}
            </button>
          </div>
        </Alerts>
      )}
      {showFixedTeamChangeAlert && (
        <Alerts title={"Aviso"} onClose={() => setShowFixedTeamChangeAlert(false)} width='max-w-md'>
          <h3 className="text-md">La tarea está fijada a su equipo. ¿Quieres continuar y cambiar el equipo asignado?</h3>

          <div className="flex justify-end space-x-3 pt-6 border-t border-line">
            <button
              type="button"
              onClick={() => {
                // Deshace el cambio de equipo y vuelve al original
                setEditTask((previousTask) => previousTask ? ({ ...previousTask, teamId: originalEditTask?.teamId ?? '' }) : previousTask);
                setShowFixedTeamChangeAlert(false);
              }}
              className="px-4 py-2 text-content bg-surface-2 rounded-md hover:bg-line"
            >
              Cancelar
            </button>
            <button
              type="button"
              className="px-6 py-2 bg-success/16 hover:bg-success/24 border border-success/60 text-success rounded-md transition-colors"
              onClick={() => {
                setShowFixedTeamChangeAlert(false);
                // Se guarda en editTask para conservarlo si luego hay que forzar por inhabilitacion
                setEditTask((previousTask) => previousTask ? ({ ...previousTask, forceUpdate: true }) : previousTask);
                handleUpdateTask(false, true);
              }}
            >
              Continuar
            </button>
          </div>
        </Alerts>
      )}
    </div>
  );
};

export default TaskManagement;

